import React, { useState } from "react";
import { Copy, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";
import VariationsMenu from "@/components/VariationsMenu";

function limitOf(p) {
  const map = {
    etsy: 140, amazon_merch: 60, redbubble: 100, teepublic: 100,
    shopify: 70, society6: 70, zazzle: 70, spreadshirt: 70, threadless: 70,
    displate: 70, merchize: 70, gelato: 70, teespring: 70,
    printful: 140, printify: 140, fourthwall: 80, gumroad: 80, sellfy: 80,
  };
  return map[p] || 140;
}

function Counter({ value, max }) {
  const len = (value || "").length;
  const pct = (len / max) * 100;
  const cls = pct > 95 ? "cc-bad" : pct > 80 ? "cc-warn" : "cc-ok";
  return <span className={"mono text-xs font-bold " + cls} data-testid="char-counter">{len}/{max}</span>;
}

function CopyBtn({ text, testId }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={async () => {
        try { await navigator.clipboard.writeText(text || ""); setOk(true); toast.success("Copied"); setTimeout(()=>setOk(false), 1200); }
        catch { toast.error("Copy failed"); }
      }}
      className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900"
    >
      {ok ? <CheckIcon size={13} className="text-[#10B981]" /> : <Copy size={13} />} Copy
    </button>
  );
}

export default function PlatformResultCard({ listing: initialListing }) {
  const [listing, setListing] = useState(initialListing);
  if (!listing) return null;
  const platform = listing.platform;
  const max = limitOf(platform);
  const tagsCsv = (listing.tags || []).join(", ");

  const setField = (field, value) => setListing(prev => ({ ...prev, [field]: value }));

  return (
    <div className="tt-card p-5 tt-fade" data-testid={`result-${platform}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Platform</div>
          <div className="text-lg font-extrabold h-tight">{listing.platformName}</div>
        </div>
        <span className="inline-block w-2 h-2 rounded-full bg-lime" />
      </div>

      <Field label="SEO Title" testId={`f-title-${platform}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm font-semibold text-zinc-900 flex-1">{listing.titleSeo}</div>
          <div className="flex flex-col items-end gap-1">
            <Counter value={listing.titleSeo} max={max} />
            <div className="flex items-center gap-3">
              <VariationsMenu field="titleSeo" listing={listing} platform={platform} onPick={(v)=>setField("titleSeo", v)} />
              <CopyBtn text={listing.titleSeo} testId={`copy-title-${platform}`} />
            </div>
          </div>
        </div>
      </Field>

      <Field label="Short description">
        <div className="text-sm text-zinc-700">{listing.descriptionShort}</div>
        <div className="mt-1 flex justify-end items-center gap-3">
          <VariationsMenu field="descriptionShort" listing={listing} platform={platform} onPick={(v)=>setField("descriptionShort", v)} />
          <CopyBtn text={listing.descriptionShort} testId={`copy-shortdesc-${platform}`} />
        </div>
      </Field>

      <Field label="Long description">
        <div className="text-sm text-zinc-700 whitespace-pre-wrap max-h-40 overflow-auto">{listing.descriptionLong}</div>
        <div className="mt-1 flex justify-end items-center gap-3">
          <VariationsMenu field="descriptionLong" listing={listing} platform={platform} onPick={(v)=>setField("descriptionLong", v)} />
          <CopyBtn text={listing.descriptionLong} testId={`copy-longdesc-${platform}`} />
        </div>
      </Field>

      {!!(listing.bullets && listing.bullets.length) && (
        <Field label="Bullets">
          <ul className="text-sm space-y-1 list-disc list-inside text-zinc-800">
            {listing.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          <div className="mt-1 flex justify-end items-center gap-3">
            <VariationsMenu field="bullets" listing={listing} platform={platform} onPick={(v)=>setField("bullets", v)} />
            <CopyBtn text={listing.bullets.join("\n")} testId={`copy-bullets-${platform}`} />
          </div>
        </Field>
      )}

      <Field label="Tags" right={<span className="mono text-[11px] text-zinc-500">{(listing.tags||[]).length} tags</span>}>
        <div className="flex flex-wrap gap-1.5">
          {(listing.tags || []).map((t, i) => (
            <span key={i} className="text-xs bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">{t}</span>
          ))}
        </div>
        <div className="mt-2 flex justify-end items-center gap-3">
          <VariationsMenu field="tags" listing={listing} platform={platform} onPick={(v)=>setField("tags", v)} />
          <CopyBtn text={tagsCsv} testId={`copy-tags-csv-${platform}`} />
        </div>
      </Field>

      {listing.altText && (
        <Field label="Image alt text">
          <div className="text-sm italic text-zinc-700">{listing.altText}</div>
          <div className="mt-1 flex justify-end items-center gap-3">
            <VariationsMenu field="altText" listing={listing} platform={platform} onPick={(v)=>setField("altText", v)} />
            <CopyBtn text={listing.altText} testId={`copy-alt-${platform}`} />
          </div>
        </Field>
      )}

      {(listing.socialCaption || (listing.hashtags && listing.hashtags.length)) && (
        <Field label="Social pack">
          {listing.socialCaption && <div className="text-sm text-zinc-800">{listing.socialCaption}</div>}
          {!!(listing.hashtags && listing.hashtags.length) && (
            <div className="mt-1 text-xs text-zinc-500">{listing.hashtags.join(" ")}</div>
          )}
          <div className="mt-1 flex justify-end items-center gap-3">
            <VariationsMenu field="socialCaption" listing={listing} platform={platform} onPick={(v)=>setField("socialCaption", v)} />
            <CopyBtn text={`${listing.socialCaption || ""}\n\n${(listing.hashtags||[]).join(" ")}`} testId={`copy-social-${platform}`} />
          </div>
        </Field>
      )}
    </div>
  );
}

function Field({ label, testId, right, children }) {
  return (
    <div className="mt-3 pt-3 border-t border-zinc-100" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
